import React, {useCallback, useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {Message} from "primereact/message";

import {SellerWorkspaceService} from "../../service/SellerWorkspaceService";
import MarketplaceIcon from "./MarketplaceIcon";
import {formatCount, formatGap} from "./sellerFormat";

const sellerWorkspaceService = new SellerWorkspaceService();

const MUTED = '#6b7280';
const cardGrid = (min) => ({display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 14});
const card = {border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', boxShadow: '0 1px 2px rgba(15, 23, 42, .04)',
    textAlign: 'left', cursor: 'pointer', width: '100%', transition: 'box-shadow .12s, border-color .12s'};

const percentOf = (part, whole) => (whole ? part / whole * 100 : null);
const formatPercent = (value) => `%${value.toLocaleString('tr-TR', {maximumFractionDigits: 1})}`;

const Headline = ({icon, value, label, share, onClick}) => (
    <button type="button" className="p-link sellers-card" style={{...card, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center'}}
            onClick={onClick}>
        {icon}
        <span>
            <div style={{fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.2}}>{value}</div>
            <div style={{color: MUTED}}>{label}</div>
            {share !== null && share !== undefined && <small style={{color: MUTED}}>{formatPercent(share)}</small>}
        </span>
    </button>
);

const Stat = ({label, value}) => (
    <div>
        <div style={{fontWeight: 600}}>{value}</div>
        <small style={{color: MUTED}}>{label}</small>
    </div>
);

// One marketplace: how many of its sellers are linked to an own seller, and what is left to review.
const MarketplaceCard = ({row, ownSellers, t, onOpen}) => {
    const coverage = percentOf(row.linked || 0, ownSellers);
    return (
        <button type="button" className="p-link sellers-card" data-marketplace={row.key} onClick={onOpen}
                style={{...card, padding: 0, display: 'flex', flexDirection: 'column'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px 0'}}>
                <MarketplaceIcon marketplaceKey={row.key} label={row.label}/>
                <span style={{fontWeight: 700, fontSize: '1.05rem', flex: 1}}>{row.label}</span>
                <i className="pi pi-angle-right" style={{color: MUTED}}/>
            </div>
            <div style={{padding: '12px 16px'}}>
                <div style={{fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2}}>{formatCount(row.linked, t)}</div>
                <div style={{color: MUTED}}>{t('sellers_workspace.summary.linked', 'Koçtaş satıcısıyla bağlı')}</div>
                {coverage !== null && <div style={{marginTop: 8}}>
                    <div style={{height: 6, borderRadius: 3, background: '#eef2f7', overflow: 'hidden'}}>
                        <div style={{width: `${Math.min(coverage, 100)}%`, height: '100%', borderRadius: 3, background: '#3b82f6'}}/>
                    </div>
                    <small style={{color: MUTED}}>
                        {t('sellers_workspace.summary.coverage', 'Koçtaş satıcılarında kapsama {{percent}}', {percent: formatPercent(coverage)})}
                    </small>
                </div>}
            </div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, padding: '10px 16px', borderTop: '1px solid #f1f5f9'}}>
                <Stat label={t('sellers_workspace.summary.suggested', 'Öneri')} value={formatCount(row.suggested, t)}/>
                <Stat label={t('sellers_workspace.summary.unmatched', 'Eşleşmedi')} value={formatCount(row.unmatched, t)}/>
                <Stat label={t('sellers_workspace.summary.known', 'Bilinen satıcı')} value={formatCount(row.known, t)}/>
            </div>
            <div style={{padding: '10px 16px 14px', borderTop: '1px solid #f1f5f9', background: '#fafbfc', borderRadius: '0 0 12px 12px', marginTop: 'auto'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', gap: 8}}>
                    <small style={{color: MUTED}}>{t('sellers_workspace.summary.medianGap', 'Aynı satıcı, medyan fiyat farkı')}</small>
                    <span style={{fontWeight: 600}}>{formatGap(row.median_gap, t)}</span>
                </div>
                {row.unreviewed > 0 &&
                    <small style={{color: MUTED}}>
                        {t('sellers_workspace.summary.unreviewed', '{{count}} eşleşme incelenmedi', {count: formatCount(row.unreviewed, t)})}
                    </small>}
            </div>
        </button>
    );
};

// Level 1: how far each marketplace's sellers are matched to the operator's own.
const MarketplaceSummary = ({t, onOpenList}) => {
    const [summary, setSummary] = useState(null);
    const [failed, setFailed] = useState(false);
    const load = useCallback(() => {
        setFailed(false);
        return sellerWorkspaceService.getSummary().then(setSummary).catch(() => setFailed(true));
    }, []);
    useEffect(() => { load(); }, [load]);

    if (failed) {
        return <div>
            <Message severity="warn" style={{display: 'block', marginBottom: 10}}
                     text={t('sellers_workspace.loadFailed', 'Satıcı bilgileri yüklenemedi.')}/>
            <Button label={t('sellers_workspace.retry', 'Tekrar dene')} className="p-button-outlined p-button-sm" onClick={load}/>
        </div>;
    }
    if (!summary) return null;

    const own = summary.own_sellers;
    const marketplaces = [...(summary.marketplaces || [])].sort((a, b) => (b.linked || 0) - (a.linked || 0));
    const linked = (key) => summary.linked?.[key] ?? 0;
    return <div>
        <div style={{...cardGrid(200), marginBottom: 18}}>
            <Headline icon={<MarketplaceIcon marketplaceKey="koctas" label="Koçtaş"/>} value={formatCount(own, t)}
                      label={t('sellers_workspace.summary.ownSellers', 'Koçtaş satıcısı')} onClick={() => onOpenList({})}/>
            <Headline icon={<MarketplaceIcon marketplaceKey="trendyol" label="Trendyol"/>} value={formatCount(linked('trendyol'), t)}
                      label={t('sellers_workspace.summary.linkedTrendyol', "Trendyol'da bağlı")} share={percentOf(linked('trendyol'), own)}
                      onClick={() => onOpenList({marketplace: 'trendyol'})}/>
            <Headline icon={<MarketplaceIcon marketplaceKey="hepsiburada" label="Hepsiburada"/>} value={formatCount(linked('hepsiburada'), t)}
                      label={t('sellers_workspace.summary.linkedHepsiburada', "Hepsiburada'da bağlı")} share={percentOf(linked('hepsiburada'), own)}
                      onClick={() => onOpenList({marketplace: 'hepsiburada'})}/>
            <Headline icon={<span style={{width: 32, height: 32, borderRadius: 8, background: '#fef3c7', color: '#b45309',
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center'}}><i className="pi pi-inbox"/></span>}
                      value={formatCount(summary.pending_suggestions, t)}
                      label={t('sellers_workspace.summary.pending', 'Onay bekleyen öneri')} onClick={() => onOpenList({filter: 'suggestions'})}/>
        </div>
        {marketplaces.length === 0
            ? <p style={{color: MUTED}}>{t('sellers_workspace.summary.empty', 'Henüz pazaryeri verisi yok.')}</p>
            : <div style={cardGrid(230)}>
                {marketplaces.map(row => <MarketplaceCard key={row.key} row={row} ownSellers={own} t={t}
                                                          onOpen={() => onOpenList({marketplace: row.key})}/>)}
            </div>}
        <p style={{color: MUTED, marginTop: 14}}>
            {t('sellers_workspace.summary.note', '"Bilinen satıcı" izlediğimiz ürünlerde görülen veya aramayla bulunan satıcıdır, pazaryerinin toplam satıcısı değildir. Fiyat farkı yalnız bağlı satıcıların izlenen ürünlerinden hesaplanır.')}
        </p>
    </div>;
};

export default MarketplaceSummary;
